import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getStatements, getStatement, uploadStatement, deleteStatement, requestCopies, money } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { FileCheck2, Upload, ClipboardPaste, Loader2, CheckCircle2, AlertTriangle, HelpCircle, Mail, Trash2, ArrowUpRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const Bucket = ({ title, icon: Icon, tone, count, value, children, testId }) => (
  <div className="rounded-lg border border-border overflow-hidden" data-testid={testId}>
    <div className={cn("flex items-center justify-between px-3 py-2 border-b border-border text-xs font-medium", tone)}>
      <span className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {title} <span className="font-mono" data-testid={`${testId}-count`}>{count}</span></span>
      {value != null && <span className="font-mono text-muted-foreground">{value}</span>}
    </div>
    <div className="max-h-52 overflow-auto d20-scroll">{children}</div>
  </div>
);

const Row = ({ left, sub, right, onClick, testId, warn }) => (
  <button data-testid={testId} onClick={onClick} disabled={!onClick} className={cn("w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-xs border-b border-border last:border-0", onClick && "hover:bg-muted/40")}>
    <div className="min-w-0"><div className="font-mono truncate">{left}</div>{sub && <div className="text-muted-foreground truncate">{sub}</div>}</div>
    <div className={cn("font-mono shrink-0 flex items-center gap-1", warn && "text-amber-500")}>{right}{onClick && <ArrowUpRight className="h-3 w-3 text-muted-foreground" />}</div>
  </button>
);

const Result = ({ st, onRequest, requesting, navigate }) => {
  const cur = st.currency;
  return (
    <div className="space-y-3" data-testid="statement-result">
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span>Statement {st.statement_date || "—"}</span>
        {st.period_start && <span>Period {st.period_start} → {st.period_end}</span>}
        <span>Closing balance <span className="font-mono text-foreground">{money(st.closing_balance, cur)}</span></span>
        <span>{st.summary.lines} lines · {st.filename}</span>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        <Bucket testId="bucket-matched" title="Matched" icon={CheckCircle2} tone="text-emerald-500" count={st.summary.matched}>
          {st.matched.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground italic">No matches.</div>}
          {st.matched.map((m, i) => (
            <Row key={m.invoice_id} testId={`matched-row-${i}`} left={m.invoice_number || m.line.invoice_number} sub={`${m.match} · ${m.status}${m.paid ? " · paid" : ""}`}
                 right={<>{money(m.line.amount, cur)}{Math.abs(m.amount_diff) > 0.01 && <span title={`D20 has ${money(m.d20_total, cur)}`}>Δ {m.amount_diff > 0 ? "+" : ""}{m.amount_diff.toFixed(2)}</span>}</>}
                 warn={Math.abs(m.amount_diff) > 0.01} onClick={() => navigate(`/invoice/${m.invoice_id}`)} />
          ))}
        </Bucket>
        <Bucket testId="bucket-missing" title="Missing in D20" icon={AlertTriangle} tone="text-amber-500" count={st.summary.missing} value={money(st.summary.missing_value, cur)}>
          {st.missing.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground italic">Nothing missing — every statement line is in D20.</div>}
          {st.missing.map((m, i) => (
            <Row key={`${m.invoice_number || "ref"}-${m.date || ""}-${m.amount}`} testId={`missing-row-${i}`} left={m.invoice_number || "(no reference)"} sub={`${m.date || ""} ${m.description || ""}`.trim()} right={`${m.type === "credit" ? "−" : ""}${money(m.amount, cur)}`} />
          ))}
        </Bucket>
        <Bucket testId="bucket-not-on" title="Not on statement" icon={HelpCircle} tone="text-sky-400" count={st.summary.not_on_statement}>
          {st.not_on_statement.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground italic">Every D20 invoice in the period appears on the statement.</div>}
          {st.not_on_statement.map((n, i) => (
            <Row key={n.invoice_id} testId={`noton-row-${i}`} left={n.invoice_number} sub={`${n.invoice_date} · ${n.status}${n.possible_duplicate ? " · possible duplicate" : ""}${n.paid ? " · paid" : ""}`}
                 right={money(n.total, n.currency)} warn={n.possible_duplicate} onClick={() => navigate(`/invoice/${n.invoice_id}`)} />
          ))}
        </Bucket>
      </div>
      {st.missing.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
          <span className="text-muted-foreground">
            {st.copy_requested_at ? <>Copies requested from <span className="font-mono text-foreground">{st.copy_requested_to}</span> on {new Date(st.copy_requested_at).toLocaleDateString()}</> : "Ask the supplier to send the missing invoices."}
          </span>
          <Button data-testid="request-copies-btn" size="sm" variant="outline" onClick={onRequest} disabled={requesting}>
            {requesting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1.5" />}
            {st.copy_requested_at ? "Ask again" : "Email supplier for copies"}
          </Button>
        </div>
      )}
    </div>
  );
};

export const StatementMatcher = ({ supplier, supplierKey }) => {
  const { user } = useApp();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [pasting, setPasting] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(() => getStatements(supplierKey).then(setList).catch(() => {}), [supplierKey]);
  useEffect(() => { load(); }, [load]);

  const run = async (payload) => {
    setBusy(true);
    try {
      const st = await uploadStatement(supplier, payload, user);
      setCurrent(st); setOpen(true); setPasting(false); setText("");
      toast.success(`Statement reconciled — ${st.summary.matched} matched, ${st.summary.missing} missing`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the statement");
    }
    setBusy(false);
  };

  const openPast = async (id) => {
    setCurrent(await getStatement(id));
    setOpen(true);
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    await deleteStatement(id);
    toast.success("Statement removed");
    load();
  };

  const request = async () => {
    setRequesting(true);
    try {
      const r = await requestCopies(current.id, user);
      toast.success(`Asked ${r.to} for ${r.count} invoice(s)`);
      setCurrent(await getStatement(current.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    }
    setRequesting(false);
  };

  const last = list[0];

  return (
    <div className="mt-4 border-t border-border pt-3" data-testid="statement-matcher">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground"><FileCheck2 className="h-3.5 w-3.5" /> Statement match</div>
        <div className="flex items-center gap-1">
          <input ref={fileRef} type="file" accept=".pdf,.csv,.txt,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="statement-file-input"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) run({ file: f }); e.target.value = ""; }} />
          <Button data-testid="statement-upload-btn" size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} disabled={busy}>
            {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />} Upload
          </Button>
          <Button data-testid="statement-paste-btn" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPasting((v) => !v)} disabled={busy}>
            <ClipboardPaste className="h-3 w-3 mr-1" /> Paste
          </Button>
        </div>
      </div>

      {pasting && (
        <div className="space-y-2 mb-2" data-testid="statement-paste-form">
          <Textarea data-testid="statement-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={5} className="text-xs font-mono"
                    placeholder={`Paste the supplier statement text here — dates, invoice numbers, amounts…`} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPasting(false)}><X className="h-3 w-3 mr-1" /> Cancel</Button>
            <Button data-testid="statement-paste-submit" size="sm" className="h-7 text-xs" onClick={() => run({ text })} disabled={busy || text.trim().length < 20}>
              {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileCheck2 className="h-3 w-3 mr-1" />} Reconcile
            </Button>
          </div>
        </div>
      )}

      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid="statement-empty">No statements yet — upload one to see what's missing.</p>
      ) : (
        <ul className="space-y-1" data-testid="statement-list">
          {list.slice(0, 3).map((st) => (
            <li key={st.id}>
              <button data-testid={`statement-row-${st.id}`} onClick={() => openPast(st.id)} className="w-full flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-muted/40 text-left">
                <span className="min-w-0 truncate">
                  <span className="font-mono">{st.statement_date || new Date(st.uploaded_at).toLocaleDateString()}</span>
                  <span className="text-muted-foreground"> · {st.summary.lines} lines · by {st.uploaded_by}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0 font-mono">
                  <span className="text-emerald-500">{st.summary.matched} ✓</span>
                  <span className={st.summary.missing ? "text-amber-500" : "text-muted-foreground"}>{st.summary.missing} missing</span>
                  <span className="text-sky-400">{st.summary.not_on_statement} extra</span>
                  <span role="button" data-testid={`statement-delete-${st.id}`} onClick={(e) => remove(e, st.id)} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-3 w-3" /></span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {last?.summary.missing > 0 && !pasting && (
        <p className="mt-1 text-[11px] text-amber-500" data-testid="statement-missing-hint">{last.summary.missing} invoice(s) on the last statement never reached D20.</p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" data-testid="statement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-primary" /> {supplier} — statement reconciliation</DialogTitle>
            <DialogDescription>Matched by invoice number first, then by amount and date (±7 days). Click a row to open the invoice.</DialogDescription>
          </DialogHeader>
          {current && <Result st={current} onRequest={request} requesting={requesting} navigate={(p) => { setOpen(false); navigate(p); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
